import { router } from "@inertiajs/react"
import { type Stripe, loadStripe } from "@stripe/stripe-js"
import { CreditCard } from "lucide-react"
import { useState } from "react"

import {
  AgentPurchaseSummary,
  type TrialInfo,
  formatUsd,
} from "@/components/billing/agent-purchase-summary"
import {
  EmbeddedPaymentForm,
  type IntentPayload,
} from "@/components/billing/stripe-payment-modal"
import { Button } from "@/components/ui/button"
import { DialogFooter } from "@/components/ui/dialog"

// IMP-389: the single "Confirm order" modal for adding an agent — used for BOTH
// first-time and repeat purchases (the old plain card sheet is gone). Shows the
// order summary, then a payment section:
//   - card on file → one-click charge; "Change" reveals the card form.
//   - no card / changing → the embedded Stripe card form (same as the landing
//     modal), with "Use the card on file instead" when a saved card exists.
// No Terms/Privacy line — the customer agreed to those at signup.
//
// IMP-394: when `trial` is set the same modal starts a free trial instead —
// $0 today, so it captures a card via a SetupIntent (mode:"setup") and starts
// the trial rather than charging. The button reads "Start free trial".

// The server's response to the one-click charge. On requires_action the card
// needs SCA/3DS: authenticate on-session, then fulfill.
export type ChargeResult =
  | { status: "succeeded"; redirect_to: string }
  | {
      status: "requires_action"
      payment_intent_id: string
      client_secret: string
      publishable_key: string
      connected_account_id: string
    }

const stripeCache = new Map<string, Promise<Stripe | null>>()
function getStripe(publishableKey: string, stripeAccount: string) {
  const key = `${publishableKey}::${stripeAccount}`
  let promise = stripeCache.get(key)
  if (!promise) {
    promise = loadStripe(publishableKey, { stripeAccount })
    stripeCache.set(key, promise)
  }
  return promise
}

// IMP-465: the dialog body, extracted so the add-job modal can render it as
// its confirmation step (no second dialog stacks over the first).
export function AgentPurchaseBody({
  active,
  onClose,
  agentName,
  monthlyCents,
  includes,
  prorated,
  dueTodayCents,
  savedCard,
  trial,
  fetchIntent,
  onCharge,
  onFulfill,
  fetchSetupIntent,
  onStartTrial,
}: {
  // Whether this step is visible (gates the embedded form's intent fetch).
  active: boolean
  onClose: () => void
  agentName: string
  monthlyCents: number
  includes: string[]
  prorated: boolean
  dueTodayCents: number
  savedCard: { brand: string; last4: string } | null
  // IMP-394: present → this is a free trial (capture a card, charge nothing).
  trial?: TrialInfo | null
  // Card-entry path: fetch the Elements intent (CreateActivationIntent).
  fetchIntent: () => Promise<IntentPayload>
  // One-click path: charge the saved card off-session.
  onCharge: () => Promise<ChargeResult>
  // Provision + navigate once a payment (either path) has completed.
  onFulfill: (paymentIntentId: string) => Promise<void>
  // IMP-394 trial: fetch the SetupIntent (card capture, $0 today).
  fetchSetupIntent?: () => Promise<IntentPayload>
  // IMP-394 trial: start the trial from a confirmed SetupIntent, or the card on
  // file (null). Returns where to navigate on success.
  onStartTrial?: (
    setupIntentId: string | null,
  ) => Promise<{ redirect_to: string }>
}) {
  // Start on the card-entry form when there's no card on file; otherwise show
  // the saved card with a "Change" affordance.
  const [changing, setChanging] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const showEntry = savedCard == null || changing
  const isTrial = trial != null

  const actionLabel = isTrial
    ? "Start free trial"
    : `Pay ${formatUsd(dueTodayCents)}`
  const cardLabel = savedCard
    ? `${titleCase(savedCard.brand)} ending ${savedCard.last4}`
    : ""

  async function payWithSavedCard() {
    setError(null)
    setProcessing(true)
    try {
      const result = await onCharge()
      if (result.status === "succeeded") {
        router.visit(result.redirect_to)
        return
      }

      // requires_action: authenticate on-session, then fulfill.
      const stripe = await getStripe(
        result.publishable_key,
        result.connected_account_id,
      )
      if (!stripe) throw new Error("Couldn't load the payment processor.")

      const { error: actionError } = await stripe.handleNextAction({
        clientSecret: result.client_secret,
      })
      if (actionError) {
        throw new Error(
          actionError.message ?? "We couldn't verify your card. Try again.",
        )
      }

      await onFulfill(result.payment_intent_id)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.")
      setProcessing(false)
    }
  }

  // IMP-394: start the trial against the card on file (one-click) or a
  // SetupIntent just confirmed in the embedded form.
  async function startTrial(setupIntentId: string | null) {
    if (!onStartTrial) return
    setError(null)
    setProcessing(true)
    try {
      const { redirect_to } = await onStartTrial(setupIntentId)
      router.visit(redirect_to)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.")
      setProcessing(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="bg-muted/40 -mx-6 space-y-4 border-y px-6 py-5">
        <AgentPurchaseSummary
          name={agentName}
          monthlyCents={monthlyCents}
          includes={includes}
          prorated={prorated}
          dueTodayCents={dueTodayCents}
          trial={trial}
        />
      </div>

      {showEntry ? (
        <EmbeddedPaymentForm
          active={active && showEntry}
          mode={isTrial ? "setup" : "payment"}
          submitLabel={actionLabel}
          fetchIntent={isTrial ? fetchSetupIntent! : fetchIntent}
          onConfirmed={isTrial ? (id) => startTrial(id) : onFulfill}
          onCancel={onClose}
          secondary={
            savedCard
              ? {
                  label: `Use ${cardLabel} instead`,
                  onClick: () => setChanging(false),
                }
              : undefined
          }
        />
      ) : (
        <>
          <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
            <div className="flex items-center gap-2.5">
              <span className="bg-muted flex size-8 items-center justify-center rounded-md">
                <CreditCard className="size-4" />
              </span>
              <span className="text-sm">{cardLabel}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => setChanging(true)}
              disabled={processing}
            >
              Change
            </Button>
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={processing}>
              Cancel
            </Button>
            <Button
              className="flex-[2]"
              onClick={() =>
                void (isTrial ? startTrial(null) : payWithSavedCard())
              }
              loading={processing}
            >
              {processing ? "Processing…" : actionLabel}
            </Button>
          </DialogFooter>
        </>
      )}
    </div>
  )
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}
